// @ts-nocheck
import * as React from "react";
import { Sidebar, Topbar, LineChart, Kpi, useCurrentUser, AppFooter, Ic, apiErrorMessage } from "./common";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { markerStyle, homeOrgMarkerStyle } from "../components/naver/markers";
import { attachMarkerTooltip, bindMapTooltipHide } from "../components/naver/tooltip";

// 미니맵 마커의 색/라벨은 markers.ts(풀 지도와 공유)가 단독으로 책임진다.

const PERIODS = [
  { key: "day",     label: "24시간" },
  { key: "week",    label: "7일" },
  { key: "month",   label: "30일" },
  { key: "quarter", label: "분기" },
];
const PERIOD_LABEL = Object.fromEntries(PERIODS.map(p => [p.key, p.label]));

// 기관(agency) 전용 대시보드 — 소속 기관(organization_id) 스코프.
// 권역 대시보드와 달리 권역 컨텍스트/실시간 알람/케이스 펑널은 없고, KPI 4종 +
// 미니맵만 노출한다. 데이터는 단일 호출(/api/v1/dashboard/agency)로 받는다.
function DashboardAgency() {
  const [data, setData]     = React.useState(null);
  const [err, setErr]       = React.useState(null);
  const [period, setPeriod] = React.useState("month");
  const { user } = useCurrentUser();

  React.useEffect(() => {
    // r.ok를 반드시 확인 — 비정상(401/403 등) 응답 본문을 데이터로 오인해
    // 렌더 중 크래시(흰 화면)하던 권역 대시보드의 버그를 반복하지 않는다.
    fetch(`/api/v1/dashboard/agency?period=${period}`, { headers: { Accept: "application/json" } })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          const err = new Error(body.message || `요청 실패 (HTTP ${r.status})`);
          (err as any).code = body?.error;
          throw err;
        }
        return r.json();
      })
      .then(setData)
      .catch(e => setErr(apiErrorMessage(e)));
  }, [period]);

  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const o = data.organization;
  const k = data.kpis;

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="agency" active="dashboard"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title={`${o.name} 기관 현황`}
                  sub={`유관기관 담당 · ${new Date().toLocaleString('ko-KR', {dateStyle:'long', timeStyle:'short'})}`}>
            <a href="/manual/agency" title="유관기관 담당 사용자 매뉴얼"
               style={{display:'inline-flex', alignItems:'center', gap:6, padding:'8px 12px', borderRadius:9, border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', fontSize:12.5, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap'}}>
              <span style={{display:'inline-flex', width:16, height:16, color:'var(--orange-600)'}}>{Ic.help}</span>
              사용자 매뉴얼
            </a>
          </Topbar>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">안녕하세요, <span style={{color:'var(--orange-600)'}}>{user?.name || '담당자'}</span>님.</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>
                  소속 케어매니저 <a href="/managers" style={{color:'var(--ink-1)', fontWeight:700, textDecoration:'underline', textDecorationStyle:'dotted', textUnderlineOffset:3}}>{o.manager_count.toLocaleString()}명</a>
                  {' · '}
                  담당 어르신 <b style={{color:'var(--ink-1)'}}>{o.elderly_count.toLocaleString()}명</b>
                  {' · '}
                  예정 방문 <a href="/schedule" style={{color:'var(--ink-1)', fontWeight:700, textDecoration:'underline', textDecorationStyle:'dotted', textUnderlineOffset:3}}>{k.planned_upcoming}건</a>
                </div>
              </div>
              <PeriodSwitch value={period} onChange={setPeriod}/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="소속 케어매니저" value={k.manager_count.toLocaleString()}/>
              <Kpi label="담당 어르신"     value={k.elderly_count.toLocaleString()} color="var(--sky-500)"/>
              <Kpi label={`${PERIOD_LABEL[period]} 완료 방문`} value={String(k.completed_visits)}
                   delta={k.completed_visits_delta} deltaDir={k.completed_visits_dir}
                   sparkline={k.sparkline_visits} color="var(--green-500)"/>
              <Kpi label="미작성 방문기록" value={String(k.unreported_reports)} color="var(--rose-500)"/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr', gap:14}}>
              <div className="card" style={{overflow:'hidden', position:'relative'}}>
                <div style={{padding:'18px 22px 6px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <div className="h3">기관 분포 · {o.name}</div>
                  <a href="/map" style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>
                    지도 보기 →
                  </a>
                </div>
                <AgencyMiniMap miniMap={data.mini_map}/>
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

// 기관 분포 미니맵 — region 대시보드의 RegionMiniMap과 동일 규약으로 가짜 SVG 투영을
// 실제 네이버 지도로 교체. mini_map.points(어르신/매니저)를 네이버 Marker로 직접 그린다.
//  - interactive=false: 카드에선 위치 확인만, 본격 줌/팬은 헤더의 "지도 보기 →"(/map)로 위임.
//  - lazy: IntersectionObserver로 카드가 뷰포트에 들어올 때만 지도 인스턴스를 마운트.
function AgencyMiniMap({ miniMap }) {
  const wrapRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [map, setMap] = React.useState(null);

  // 카드가 화면(+200px 선행 마진)에 들어오면 1회만 지도를 마운트. 미지원 환경은 즉시 폴백.
  React.useEffect(() => {
    if (inView) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const center = miniMap?.center;
  const b = miniMap?.bounds;
  // 백엔드 bounds(lat_min/max·lng_min/max) → NaverMapCanvas의 bbox(north/south/east/west).
  const bbox = b ? { north: b.lat_max, south: b.lat_min, east: b.lng_max, west: b.lng_min } : null;

  // points 내용이 실제로 바뀔 때만 마커를 재생성하기 위한 시그니처(폴링 깜빡임 방지).
  const points = miniMap?.points || [];
  const sig = React.useMemo(
    () => JSON.stringify(points.map(p => [p.kind, p.id, p.lat, p.lng, p.risk_level, p.status])),
    [points]
  );

  // map 인스턴스가 잡히면 마커를 그린다. map/points 시그니처 변화마다 전량 재생성.
  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;
    // 모양/색/툴팁은 풀 지도(/map)와 같은 단일 출처(markers.ts). 소속 기관만
    // 전용 강조 마커(homeOrgMarkerStyle)로 구분한다.
    const markers = points.map((p) => {
      if (p.lat == null || p.lng == null) return null;
      const isAgency = p.kind === 'agency';
      const style = isAgency
        ? homeOrgMarkerStyle(p, { compact: true })
        : markerStyle(p.kind === 'elderly' ? 'elderly' : 'manager', p, { compact: true });
      // 우리 기관 마커는 위치 표식용이라 상세 이동 없음. 어르신/매니저만 상세로 링크.
      const detailHref = isAgency ? null : (p.id ? (p.kind === 'elderly' ? `/elderly?id=${p.id}` : `/managers?id=${p.id}`) : null);
      const marker = new naver.maps.Marker({
        map,
        position: new naver.maps.LatLng(p.lat, p.lng),
        zIndex: style.zIndex,
        icon: {
          content: style.content,
          anchor: new naver.maps.Point(style.anchorX, style.anchorY),
        },
      });
      attachMarkerTooltip(naver, marker, style.tooltip);
      if (detailHref) {
        naver.maps.Event.addListener(marker, "click", () => { window.location.href = detailHref; });
      }
      return marker;
    }).filter(Boolean);
    const unbindTooltip = bindMapTooltipHide(naver, map);
    return () => {
      unbindTooltip();
      markers.forEach(m => { try { m.setMap(null); } catch {} });
    };
    // sig가 points 내용을 대표하므로 points 참조 자체는 dep에서 제외.
  }, [map, sig]);

  return (
    <div ref={wrapRef}
      style={{height:300, margin:'8px 16px 16px', borderRadius:12, background:'linear-gradient(135deg, #E7E1CF, #D9D0B6)', position:'relative', overflow:'hidden'}}>
      {inView && center
        ? <NaverMapCanvas center={center} bbox={bbox} zoom={14} interactive={false} fitPadding={28} minFitZoom={13} onMapReady={setMap}/>
        : <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
            <span className="muted" style={{fontSize:12, fontWeight:600, background:'rgba(255,255,255,0.85)', padding:'6px 12px', borderRadius:8}}>지도 준비 중…</span>
          </div>}
      {points.length === 0 && (
        <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center', color:'var(--ink-3)', fontSize:12, pointerEvents:'none'}}>
          표시할 위치 데이터가 없습니다.
        </div>
      )}
      {/* 범례 — 지도 위 고정 오버레이. pointerEvents 차단으로 마커 클릭을 방해하지 않음. */}
      <div style={{position:'absolute', bottom:10, left:10, zIndex:10, pointerEvents:'none', background:'rgba(255,255,255,0.92)', borderRadius:10, padding:'6px 10px', fontSize:10, display:'flex', gap:10}}>
        <span><span className="dot" style={{background:'var(--orange-500)'}}/> 우리 기관</span>
        <span><span className="dot" style={{background:'var(--orange-500)'}}/> 매니저</span>
        <span><span className="dot" style={{background:'var(--rose-500)'}}/> 위험</span>
        <span><span className="dot" style={{background:'var(--amber-500)'}}/> 주의</span>
        <span><span className="dot" style={{background:'var(--green-500)'}}/> 안정</span>
      </div>
    </div>
  );
}

// 기간 선택 — 단일 refetch로 period-스코프 위젯(완료 방문 KPI/sparkline)을 갱신.
function PeriodSwitch({ value, onChange }) {
  return (
    <div role="tablist" aria-label="기간 선택"
      style={{display:'inline-flex', background:'var(--bg-sub)', borderRadius:10, padding:3, border:'1px solid var(--border-soft)'}}>
      {PERIODS.map(p => {
        const active = p.key === value;
        return (
          <button key={p.key} role="tab" aria-selected={active} onClick={()=>onChange(p.key)}
            style={{
              padding:'6px 12px', fontSize:12, fontWeight:700,
              border:'none', borderRadius:7, cursor:'pointer',
              background: active ? 'var(--surface)' : 'transparent',
              color:      active ? 'var(--ink-1)'   : 'var(--ink-3)',
              boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              transition: 'background 0.15s, color 0.15s',
            }}>
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export { DashboardAgency };
